import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { FetchErrorCallout } from '@/components/ui/fetch-error-callout';

interface PatientRecordErrorStateProps {
  readonly error: Error;
  readonly onRetry: () => void;
}

/**
 * Page-level error for unrecoverable patient detail fetch failures.
 * `not_found` is handled separately by the route (renders the global
 * NotFoundPage); everything else lands here.
 */
export function PatientRecordErrorState({
  error,
  onRetry,
}: PatientRecordErrorStateProps): ReactElement {
  const { t } = useTranslation();
  return (
    <div className="p-6 max-w-xl mx-auto">
      <FetchErrorCallout
        error={error}
        heading={t('routes.patient.error.heading')}
        message={t('routes.patient.error.message')}
        codeLabel={t('routes.patient.error.codeLabel')}
        headingAs="h2"
        actions={
          <>
            <Button onClick={onRetry}>{t('routes.patient.error.retry')}</Button>
            <Button variant="outline" asChild>
              <Link to="/my-cases">{t('routes.patient.error.backToHome')}</Link>
            </Button>
          </>
        }
      />
    </div>
  );
}
